import split from "lodash/split"
import get from "lodash/get"
import replace from "lodash/replace"
import filter from "lodash/filter"
import toLower from "lodash/toLower"
import toArray from "lodash/toArray"
import mapValues from "lodash/mapValues"
import map from "lodash/map"
import each from "lodash/each"
import isEmpty from "lodash/isEmpty"
import uniq from "lodash/uniq"
import toString from "lodash/toString"
import set from "lodash/set"
import join from "lodash/join"
import concat from "lodash/concat"
import getLanguageRoute from "utils-lib/getLanguageRoute"
import { getDeviceChannel } from "utils-lib/getDeviceChannel"
import { userType } from "constants-lib/authentication"
import { localStorageKey } from "constants-lib/localStorage"
import { getWindow } from "utils-lib/getWindow"
import { getSessionStorage } from "./isBot"

const ssrWindow = getWindow()

/* page structure */
// const page = {
//   site_section: ``,
//   pagename: ``,
//   pagename_concat: ``, // site_section|pagename
//   previous_page_name: ``, // previous pagename_concat
//   country_language: ``,
//   device: ``,
//   base_URL: `wm.com`,
// }

/* eslint-disable no-underscore-dangle */
/* eslint-disable camelcase */
/* eslint-disable no-use-before-define */
/* eslint-disable no-unused-expressions */
/* eslint-disable consistent-return */

/**
 * Returns the site section based on the current URL pathname and language.
 * @param {string} language - The language code for the current page.
 * @returns {string} - The site section name.
 */
export function getSiteSection(language) {
  const { pathname } = ssrWindow.location
  const languageRoute = getLanguageRoute(language)

  let site_section = ``

  if (pathname.indexOf(languageRoute) === -1 || pathname === languageRoute) {
    site_section = `homepage`
  } else {
    const splitBySlash = split(ssrWindow.location.pathname, `/`)
    site_section =
      splitBySlash.length < 4 ? `blank` : get(splitBySlash, `[3]`, ``)
  }

  return replace(site_section, `-`, `_`)
}

/**
 * Returns the page name based on the current URL and language.
 * @param {string} language - The language code for the current page.
 * @returns {string} - The page name.
 */
export function getPagename(language) {
  const { pathname } = ssrWindow.location
  const languageRoute = getLanguageRoute(language)

  let pagename = ``

  if (pathname.indexOf(languageRoute) === -1 || pathname === languageRoute) {
    pagename = `homepage`
  } else {
    const splitBySlash = split(ssrWindow.location.pathname, `/`)
    const length = get(splitBySlash, `length`, 0)
    pagename =
      splitBySlash.length === 0
        ? `blank`
        : get(splitBySlash, `[${length - 1}]`, ``)
  }

  return replace(pagename, `-`, `_`)
}

/**
 * Gets the value of a URL parameter from the current page's URL.
 * @function
 * @param {string} paramName - The name of the URL parameter to get the value of.
 * @returns {string} The value of the URL parameter or an empty string if the parameter is not found.
 */
export const getDLVarFromParam = (paramName) =>
  split(
    filter(
      split(ssrWindow.location.search, `&`),
      (str) => str.indexOf(paramName) > -1,
    )[0],
    `=`,
  )[1] || ``

/**
 * Updates the page object in the global `_dl` object.
 * @function
 * @param {Object} params - An object containing the following properties:
 * @param {string} params.language - The language of the page.
 * @param {string} [params.previous_page_name=""] - The name of the previous page.
 * @param {string} [params.templateName=""] - The name of the template used for the page.
 * @param {string} [params.lobType=""] - The type of line of business for the page.
 * @param {string} [params.gisIDs=""] - The GIS IDs for the page.
 */
export const updatePageDL = ({
  language,
  previous_page_name = ``,
  templateName = ``,
  lobType = ``,
  gisIDs = ``,
}) => {
  if (!ssrWindow._dl) resetDataLayer()
  if (ssrWindow._dl && !ssrWindow._dl.page) resetDataLayer([`page`])

  const site_section = getSiteSection(language)
  const pagename = getPagename(language)
  const defaultConsent = {
    "banner-available": true,
    "Non-Essential": false,
    "Session Replay": false,
  }
  const defaultNoConsent = {
    ...defaultConsent,
    "banner-available": false,
  }
  const availableConsent = JSON?.parse(
    ssrWindow?.localStorage?.getItem("consents-lib"),
  )
  const isQuebec = ssrWindow.document.getElementsByClassName("cmp-loader")

  // update local storage of current page name for cross subdomain updates
  ssrWindow.localStorage.setItem(localStorageKey.DL_PAGE_NAME, pagename)

  const updatedConsent = {
    ...availableConsent,
    "banner-available": true,
  }

  const consents = availableConsent
    ? updatedConsent
    : (isQuebec?.length > 0 && defaultConsent) || defaultNoConsent

  ssrWindow._dl.page = {
    site_section,
    pagename,
    pagename_concat: `${site_section}|${pagename}`,
    previous_page_name,
    country_language: toLower(language),
    device: toLower(getDeviceChannel()),
    base_URL: ssrWindow.location.origin,
    templateName,
    lobType,
    gisIDs,
    consents,
  }

  updateExperienceCloudID()
}

/* clicks item structure */
// const clicks = {
//   ui_element: ``,
//   object_content: ``,
// }

/**
 * Updates the clicks object in the global `_dl` object.
 * @function
 * @param {Object} params - An object containing the following properties:
 * @param {string} params.ui_element - The UI element that was clicked.
 * @param {object} params.object_content - The content of the clicked object.
 * @param {object} params[...rest] - Any additional click objects requested for specific analytics.
 */
export const updateClicksDL = ({ ui_element, object_content, ...rest }) => {
  if (!ssrWindow._dl) {
    resetDataLayer()
  } else if (!ssrWindow._dl.clicks) {
    resetDataLayer([`clicks`])
  }

  // update local storage of current page name for cross subdomain updates
  ssrWindow.localStorage.setItem(localStorageKey.DL_UI_ELEMENT, ui_element)
  ssrWindow.localStorage.setItem(
    localStorageKey.DL_OBJECT_CONTENT,
    object_content,
  )

  ssrWindow._dl.clicks = {
    ui_element,
    object_content,
    ...rest,
  }
}

/* visitor structure */
// const visitor = {
//   experience_cloud_id: ``, // ssrWindow.s.marketingCloudVisitorID
//   target_id: ``, // ssrWindow.s.supplementalDataID
//   wm_user_id: ``, // user profile id
//   wm_customer_id: [], // customer id list
//   is_logged_in: ``,
//   is_repeat: ``,
//   customer_type: ``, // TRO, SMB, OMR, LN
//   lob: ``, // ROLLOFF, COMMERCIAL, RESIDENTIAL, LANDFILL
//   customer_list: []
// }

/**
 * Returns an array of customer account IDs linked to the given user's account.
 *
 * @param {Object} userManageAccount - The user's account object.
 * @returns {Array} - An array of customer account IDs.
 */
export function getCustomerIds(userManageAccount) {
  return get(userManageAccount, `linkedAccounts`, ``)
    ? toArray(
        mapValues(
          get(userManageAccount, `linkedAccounts`, ``),
          `custAccountId`,
        ),
      )
    : []
}

/**
 * Returns an array of customer types based on the provided list of lines of business.
 * @param {string[]} lobList - The list of lines of business to map to customer types.
 * @returns {string[]} An array of customer types.
 */
export function getCustomerType(lobList) {
  return map(lobList, (lob) => {
    switch (lob) {
      case `landfill`:
        return `ln`
      case `rolloff`:
        return `tro`
      case `commercial`:
        return `smb`
      case `residential`:
        return `omr`
      default:
    }
  })
}

/**
 * Returns an array of unique and sorted LOBs (Line of Business) associated with the given user's managed account.
 * @param {Object} userManageAccount - The user's managed account object.
 * @returns {(string[]|string)} - An array of unique and sorted LOBs or an empty string if no LOBs are found.
 */
export function getLOB(userManageAccount) {
  const lobs = []
  each(get(userManageAccount, `linkedAccounts`, ``), (acct) => {
    lobs.push(toLower(get(acct, `lob`)))
  })

  return !isEmpty(lobs) ? uniq(lobs).sort() : ``
}

/**
 * Groups services by account.
 *
 * @param {Object} myServices - The services object to group by account.
 * @returns {Object} An object with services grouped by account.
 */
export const pivotServicesByAccount = (myServices) => {
  const servicesByAccount = {}

  each(get(myServices, `services`, []), (serviceGroup) => {
    const lineOfBusiness = get(serviceGroup, `lineOfBusiness`, ``)
    const services = []
    each(get(serviceGroup, `services`, []), (service) => {
      services.push({
        serviceId: get(service, `serviceId`, ``),
        lineOfBusiness,
        rollOffType: get(service, `rollOffType`, `N/A`),
      })
    })
    servicesByAccount[get(serviceGroup, `ezpayId`, ``)] = services
  })
  return servicesByAccount
}

/* customer item structure */
// customer_list: {
//   [customer_id]: {
//     category: ``,
//     status: ``,
//     autopay: ``,
//     paperless: ``,
//     services: [
//       lineOfBusiness: ``,
//       rollOffType: ``,
//     ]
//   }, ...
// }

/**
 * Updates the customer list in the global `_dl.visitor.customer_list` object.
 * @function
 * @param {Object} params - An object containing the following properties:
 * @param {Object} params.userManageAccount - The user manage account object.
 * @param {Object} params.customerAutoPayPaperless - The customer auto pay paperless object.
 * @param {Object} params.myServices - The my services object.
 * @param {boolean} [shouldUpdate=true] - A flag indicating whether to update the global `_dl.visitor.customer_list` object.
 * @returns {Object} The updated customer list object.
 */
export const updateCustomerListDL = (
  { userManageAccount, customerAutoPayPaperless, myServices },
  shouldUpdate = true,
) => {
  const customer_list = {}
  let ap_pl = {}
  let lob_perm_temp = {}

  if (!isEmpty(customerAutoPayPaperless)) {
    ap_pl = get(customerAutoPayPaperless, `attributesDetailsByAccount`, {})
  }

  if (!isEmpty(myServices)) {
    lob_perm_temp = pivotServicesByAccount(myServices)
  }

  if (!isEmpty(ap_pl) || !isEmpty(lob_perm_temp)) {
    each(userManageAccount.userAccountDetails, (cust) => {
      const id = toString(get(cust, `custAccountId`, ``))
      // autopay paperless
      const appl = get(ap_pl, id, {})
      const autopay =
        get(appl, `autopayStatus`, ``) ||
        get(ssrWindow, `_dl.visitor.customer_list[${id}].autopay`, ``)
      const paperless =
        get(appl, `paperlessStatus`, ``) ||
        get(ssrWindow, `_dl.visitor.customer_list[${id}].autopay`, ``)

      // services
      const services = get(lob_perm_temp, id, [])
      customer_list[id] = {
        category: get(cust, `category`, ``),
        status: get(cust, `status`, ``),
        autopay,
        paperless,
        services: !isEmpty(services)
          ? services
          : get(ssrWindow, `_dl.visitor.customer_list[${id}].services`, []),
      }
    })

    shouldUpdate && set(ssrWindow, `_dl.visitor.customer_list`, customer_list)

    return get(ssrWindow, `_dl.visitor.customer_list`, customer_list)
  }

  return get(ssrWindow, `_dl.visitor.customer_list`, {})
}

/**
 * Updates the visitor data layer with the provided information.
 * @param {Object} options - The options object.
 * @param {Object} options.userAccount - The user account information.
 * @param {Object} options.userManageAccount - The user manage account information.
 * @param {Object} [options.customerAutoPayPaperless={}] - The customer auto pay paperless information.
 * @param {Object} [options.myServices={}] - The my services information.
 * @param {Object} [options.customers={}] - The customers information.
 * @param {Object} [options.accountAttributesApi={}] - The account attributes API information.
 * @param {Object} [options.pickupInfoApi={}] - The pickup info API information.
 * @param {Object} [options.serviceDetailsApi={}] - The service details API information.
 * @param {Object} [options.eligibilityApi={}] - The eligibility API information.
 * @param {Object} [options.customerSearchApi={}] - The customer search API information.
 * @param {Object} [options.extraPickupEligibility={}] - The extra pickup eligibility information.
 */
export const updateVisitorDL = ({
  userAccount,
  userManageAccount,
  customerAutoPayPaperless = {},
  myServices = {},
  customers = {},
  accountAttributesApi = {},
  pickupInfoApi = {},
  serviceDetailsApi = {},
  eligibilityApi = {},
  customerSearchApi = {},
  extraPickupEligibility = {},
  countOfCustomers = get(ssrWindow, "_dl.visitor.countOfCustomers", 0),
  rebateOfferComponentShown = get(
    ssrWindow,
    "_dl.visitor.rebateOfferComponentShown",
    false,
  ),
  products = get(ssrWindow, "_dl.visitor.products", []),
}) => {
  if (!ssrWindow._dl) {
    resetDataLayer()
  } else if (!ssrWindow._dl.visitor) {
    resetDataLayer([`visitor`])
  }

  const reduxUserState = get(userAccount, `userState`, ``)
  const localStorageUserState = get(
    ssrWindow,
    `localStorage.${localStorageKey.USER_STATE}`,
  )

  const lob = getLOB(userManageAccount)
  const botdResult = JSON.parse(getSessionStorage("botdResult"))
  const getId = getSessionStorage("ID")
  const sessionId = isEmpty(getId) ? "" : getId

  const customer_type = getCustomerType(lob)
  ssrWindow._dl.visitor = {
    target_id: get(ssrWindow, `s.supplementalDataID`, ``),
    wm_user_id: get(userAccount, `userDetails.userId`, ``), // user profile id
    wm_customer_id: getCustomerIds(userManageAccount), // customer id list
    is_logged_in:
      reduxUserState === userType.LOGGED_IN ||
      localStorageUserState === userType.LOGGED_IN
        ? `yes`
        : `no`,
    is_repeat:
      reduxUserState === userType.COOKIED ||
      localStorageUserState === userType.COOKIED
        ? `yes`
        : `no`,
    customer_type: join(customer_type, `|`),
    lob: join(lob, `|`),
    customer_list: updateCustomerListDL(
      { userManageAccount, customerAutoPayPaperless, myServices },
      false,
    ),
    is_bot: get(botdResult, "bot", ``),
    customers,
    sessionId,
    accountAttributesApi,
    pickupInfoApi,
    serviceDetailsApi,
    eligibilityApi,
    customerSearchApi,
    extraPickupEligibility,
    countOfCustomers,
    rebateOfferComponentShown,
    products,
  }

  updateExperienceCloudID()
}

/**
 * Updates the experience cloud ID in the data layer based on the marketing cloud visitor ID.
 *
 * @returns {void}
 */
export const updateExperienceCloudID = () => {
  const dl_ecid = get(ssrWindow, `_dl.visitor.experience_cloud_id`, ``)
  const ecid = get(ssrWindow, `s.marketingCloudVisitorID`, ``)
  // if datalayer ecid is not empty
  //  update to ecid
  // else if datalayer ecid is not empty AND ecid is not empty
  //  if ecid and datalayer ecid are different
  //    update to ecid
  //  else
  //    do not update
  if (!isEmpty(dl_ecid) || (!isEmpty(ecid) && dl_ecid !== ecid)) {
    set(ssrWindow, `_dl.visitor.experience_cloud_id`, ecid)
  }
}

/* cart structure */
// const blankCart = [{
//   lob: ``, // categoryId
//   product_name: ``, // productName
//   product_sku: ``, // productId
//   product_lob: `tro`, // AEM only handles TRO
//   quantity: `1`, // must be string, always 1 for TRO
//   price: ``, // must be string
//   currency: ``, // USD, CAD
// }, ...]

/**
 * This function updates the cart in the data layer.
 * @param {Object} cartObj - The cart object to be added to the data layer.
 * @param {boolean} [saveToCart=false] - A flag indicating whether to save the cart object to the data layer.
 */
export const updateCartDL = (cartObj, saveToCart = false) => {
  if (!ssrWindow._dl) {
    resetDataLayer()
  } else if (!ssrWindow._dl.visitor) {
    resetDataLayer([`cart`])
  }

  const newCart = get(ssrWindow._dl, `cart`, [])
  if (!saveToCart) {
    newCart.pop()
  }
  ssrWindow._dl.cart = concat([], newCart, [{ ...cartObj, quantity: "1" }])
}

/**
 * An object representing a blank data layer.
 * @typedef {Object} blankDL
 * @property {Object} page - Contains properties related to the current page.
 * @property {Object} clicks - Contains properties related to user clicks.
 * @property {Object} visitor - Contains properties related to the visitor.
 * @property {Array} cart - An array representing the visitor's cart.
 */
export const blankDL = {
  page: {
    site_section: ``,
    pagename: ``,
    pagename_concat: ``, // site_section|pagename
    previous_page_name: ``, // previous pagename
    country_language: ``,
    device: ``,
    base_URL: `wm.com`,
  },
  clicks: {
    ui_element: ``,
    object_content: ``,
  },
  visitor: {
    experience_cloud_id: ``,
    wm_customer_id: ``,
    target_id: ``,
    is_logged_in: ``,
    is_repeat: ``,
    customer_type: ``,
    lob: ``,
    customer_list: {},
    customers: {},
    is_bot: ``,
    accountAttributesApi: {},
    pickupInfoApi: {},
    serviceDetailsApi: {},
    eligibilityApi: {},
    customerSearchApi: {},
    extraPickupEligibility: {},
    countOfCustomers: "",
    rebateOfferComponentShown: "",
    products: [],
  },
  cart: [],
}

/**
 * Updates the data layer with various parameters.
 * @param {Object} options - The options for updating the data layer.
 * @param {string} options.previous_page_name - The name of the previous page.
 * @param {string} options.language - The language code for the current page.
 * @param {Object} options.userAccount - User account information.
 * @param {Object} options.userManageAccount - User manage account information.
 * @param {Object} options.customerAutoPayPaperless - Customer auto pay and paperless information.
 * @param {Object} options.myServices - Information about the user's services.
 * @param {Object} options.clicks - Information about the user's clicks.
 * @param {string} options.templateName - The name of the current template.
 * @param {string} options.lobType - The type of line of business.
 * @param {Array} options.gisIDs - An array of GIS IDs.
 * @param {Array} options.customers - An array of customer information.
 * @param {Object} options.accountAttributesApi - Information from the account attributes API.
 * @param {Object} options.pickupInfoApi - Information from the pickupInfoApi API.
 * @param {Object} options.serviceDetailsApi - Information from the details API.
 * @param {Object} options.eligibilityApi - Information from the eligibility API.
 * @param {Object} options.customerSearchApi - Information from the customer search API.
 * @param {Object} options.extraPickupEligibility - Information about extra pickup eligibility.
 */
export const updateDataLayer = ({
  previous_page_name = ``,
  language,
  userAccount,
  userManageAccount,
  customerAutoPayPaperless,
  myServices,
  clicks,
  templateName,
  lobType,
  gisIDs,
  customers,
  accountAttributesApi,
  pickupInfoApi,
  serviceDetailsApi,
  eligibilityApi,
  customerSearchApi,
  extraPickupEligibility,
}) => {
  updatePageDL({ language, previous_page_name, templateName, lobType, gisIDs })
  updateVisitorDL({
    userAccount,
    userManageAccount,
    customerAutoPayPaperless,
    myServices,
    customers,
    accountAttributesApi,
    pickupInfoApi,
    serviceDetailsApi,
    eligibilityApi,
    customerSearchApi,
    extraPickupEligibility,
  })
  !isEmpty(clicks) && updateClicksDL(clicks)
}

/**
 * Resets the data layer to its default state or specific sections.
 * @param {string[]} [sections=[]] - The sections of the data layer to reset.
 */
export const resetDataLayer = (sections = []) => {
  // page, clicks, visitor, cart
  if (isEmpty(sections)) {
    ssrWindow._dl = blankDL
  } else {
    each(sections, (section) => {
      ssrWindow._dl[section] = blankDL[section]
    })
  }
}
